import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Mail, Lock, ArrowRight, ShieldCheck, Activity, Globe } from "lucide-react";
import { DravianLogo } from "@/components/dravian/logo";
import { FooterStrip } from "@/components/dravian/footer-strip";
import { ApiError, auth } from "@/lib/api";
import { AUTH_MODE } from "@/lib/auth";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Dravian Horizon — Operational Risk Intelligence" },
      { name: "description", content: "Sign in to Dravian Horizon — operational risk intelligence for geopolitical, supply-chain, financial, and threat domains." },
      { property: "og:title", content: "Dravian Horizon — Operational Risk Intelligence" },
      { property: "og:description", content: "From signal to scenario — see the whole graph at once." },
      { property: "og:url", content: "/" },
    ],
    links: [{ rel: "canonical", href: "/" }],
  }),
  component: LoginPage,
});

type AuthMode =
  | "signin"
  | "signup"
  | "confirm"
  | "forgot"
  | "forgot-confirm"
  | "set-new-password";

function LoginPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const cognitoOn = AUTH_MODE === "cognito";

  // After Cognito accepts the credentials we still need to confirm the user
  // is authorized for THIS Horizon instance (must have the `customer` group;
  // eventually also the right organization_id from the License Service). If
  // the backend rejects, surface its message right here so the user knows
  // why instead of silently bouncing back to /.
  const completeLogin = async () => {
    try {
      await qc.invalidateQueries({ queryKey: ["me"] });
      const me = await auth.me(); // throws ApiError(401) if the backend rejects
      // Pre-seed the cache so the route guard doesn't refetch on /hq.
      qc.setQueryData(["me"], me);
      navigate({ to: "/hq" });
    } catch (err) {
      // Strip the tokens — they're useless for this instance.
      if (cognitoOn) {
        const m = await import("@/lib/cognito-srp");
        m.srpSignOut();
      }
      const { _setAccessToken } = await import("@/lib/auth");
      _setAccessToken(null);
      // Re-throw so the calling submit() handler shows the message.
      throw err;
    }
  };

  const [email, setEmail] = useState(cognitoOn ? "" : "founder@dravian.io");
  const [password, setPassword] = useState(cognitoOn ? "" : "horizon-dev-password");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [mode, setMode] = useState<AuthMode>("signin");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const reset = () => {
    setError(null);
    setInfo(null);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    reset();
    setLoading(true);
    try {
      if (!cognitoOn) {
        // Legacy LocalAuthAdapter path.
        await auth.login(email, password);
        await completeLogin();
        return;
      }

      const srp = await import("@/lib/cognito-srp");

      if (mode === "signin") {
        const result = await srp.srpSignIn(email, password);
        // Admin-created users land here on first login: Cognito demands a new
        // password before issuing tokens. Switch to the set-new-password step.
        if (result.newPasswordRequired) {
          setInfo(
            "First sign-in: choose a new password to replace the temporary one.",
          );
          setNewPassword("");
          setMode("set-new-password");
          return;
        }
        await completeLogin();
      } else if (mode === "set-new-password") {
        await srp.srpCompleteNewPassword(newPassword, name);
        await completeLogin();
      } else if (mode === "signup") {
        const res = await srp.srpSignUp(email, password, name || email.split("@")[0]);
        if (res.needsConfirmation) {
          setInfo("Check your email for a 6-digit code.");
          setMode("confirm");
        } else {
          // Auto sign-in if Cognito didn't require confirmation
          await srp.srpSignIn(email, password);
          await completeLogin();
        }
      } else if (mode === "confirm") {
        await srp.srpConfirmSignUp(email, code.trim());
        setInfo("Confirmed. Signing you in…");
        await srp.srpSignIn(email, password);
        await completeLogin();
      } else if (mode === "forgot") {
        await srp.srpForgotPassword(email);
        setInfo("Check your email for a reset code.");
        setMode("forgot-confirm");
      } else if (mode === "forgot-confirm") {
        await srp.srpConfirmForgotPassword(email, code.trim(), newPassword);
        setInfo("Password reset. Signing you in…");
        await srp.srpSignIn(email, newPassword);
        await completeLogin();
      }
    } catch (err) {
      // ApiError 401 from /auth/me usually carries the real reason from the
      // backend (e.g., "Account is not authorized…"). Pass it through instead
      // of showing a generic "Invalid email or password" — those two failures
      // need different recovery actions.
      let msg: string;
      if (err instanceof ApiError && err.status === 401) {
        const body = err.body as { detail?: string } | string | undefined;
        const detail =
          typeof body === "object" && body && "detail" in body ? body.detail : undefined;
        msg = detail || "Invalid email or password.";
      } else if (err instanceof Error) {
        msg = err.message;
      } else {
        msg = "Login failed.";
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    reset();
    try {
      const srp = await import("@/lib/cognito-srp");
      await srp.srpResendConfirmation(email);
      setInfo("Sent a new code to your email.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't resend code.");
    }
  };

  const heading = (() => {
    if (!cognitoOn) return "Sign in to Horizon";
    if (mode === "signin") return "Sign in to Horizon";
    if (mode === "signup") return "Create your Horizon account";
    if (mode === "confirm") return "Confirm your email";
    if (mode === "forgot") return "Reset your password";
    if (mode === "set-new-password") return "Choose your password";
    return "Set a new password";
  })();
  const subheading = (() => {
    if (!cognitoOn)
      return "Operational risk intelligence — restricted to authorized analysts.";
    if (mode === "signin")
      return "Operational risk intelligence — restricted to authorized analysts.";
    if (mode === "signup") return "We'll send a verification code to your email.";
    if (mode === "confirm") return "Enter the 6-digit code we just emailed you.";
    if (mode === "forgot") return "We'll email you a code to set a new password.";
    if (mode === "set-new-password")
      return "Your account was created with a temporary password. Pick a permanent one.";
    return "Use the code we just emailed you to set a new password.";
  })();
  const ctaLabel = (() => {
    if (loading) return "…";
    if (!cognitoOn) return "Sign in";
    if (mode === "signin") return "Sign in";
    if (mode === "signup") return "Create account";
    if (mode === "confirm") return "Confirm";
    if (mode === "forgot") return "Send code";
    if (mode === "set-new-password") return "Save and sign in";
    return "Set new password";
  })();

  return (
    <div className="grid min-h-screen lg:grid-cols-2 bg-background">
      {/* Left — form */}
      <div className="flex flex-col px-6 py-8 sm:px-10">
        <div className="flex items-center justify-between">
          <DravianLogo />
          <a
            href="https://www.dravian.io/contact"
            target="_blank"
            rel="noreferrer"
            className="text-[12px] text-muted-foreground hover:text-foreground"
          >
            Request access →
          </a>
        </div>

        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-[380px]">
            <div className="label-eyebrow mb-2">Secure access</div>
            <h1 className="text-[28px] font-semibold tracking-tight">{heading}</h1>
            <p className="text-[13px] text-muted-foreground mt-1.5">{subheading}</p>

            <form onSubmit={submit} className="mt-8 space-y-3">
              {/* Email — shown in every mode. Disabled when we already know it. */}
              {(mode === "signin" ||
                mode === "signup" ||
                mode === "forgot" ||
                mode === "confirm" ||
                mode === "forgot-confirm" ||
                mode === "set-new-password") && (
                <Field label="Email" icon={Mail}>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="analyst@dravian.io"
                    disabled={
                      mode === "confirm" ||
                      mode === "forgot-confirm" ||
                      mode === "set-new-password"
                    }
                    className="w-full bg-transparent text-[13px] outline-none placeholder:text-muted-foreground/60 disabled:opacity-70"
                  />
                </Field>
              )}

              {/* Name — signup, and the first-login set-new-password where
                  Cognito requires us to fill the missing `name` attribute. */}
              {(mode === "signup" || mode === "set-new-password") && (
                <Field label="Name" icon={Mail}>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Santiago Carvajal"
                    className="w-full bg-transparent text-[13px] outline-none placeholder:text-muted-foreground/60"
                  />
                </Field>
              )}

              {/* Password — sign-in, sign-up */}
              {(mode === "signin" || mode === "signup") && (
                <Field label="Password" icon={Lock}>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={
                      mode === "signup" ? "8+ chars, upper, lower, digit, symbol" : "••••••••"
                    }
                    className="w-full bg-transparent text-[13px] outline-none placeholder:text-muted-foreground/60"
                  />
                </Field>
              )}

              {/* Confirmation code */}
              {(mode === "confirm" || mode === "forgot-confirm") && (
                <Field label="Confirmation code" icon={Lock}>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="123456"
                    className="w-full bg-transparent text-[13px] outline-none placeholder:text-muted-foreground/60 font-mono tracking-wider"
                  />
                </Field>
              )}

              {/* New password — forgot-confirm or first-login set-new-password */}
              {(mode === "forgot-confirm" || mode === "set-new-password") && (
                <Field label="New password" icon={Lock}>
                  <input
                    type="password"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="8+ chars, upper, lower, digit, symbol"
                    className="w-full bg-transparent text-[13px] outline-none placeholder:text-muted-foreground/60"
                  />
                </Field>
              )}

              {/* Action row */}
              {mode === "signin" && (
                <div className="flex items-center justify-between text-[11px] pt-1">
                  <label className="inline-flex items-center gap-1.5 text-muted-foreground cursor-pointer">
                    <input type="checkbox" className="accent-primary" defaultChecked /> Trust this device
                  </label>
                  {cognitoOn && (
                    <button
                      type="button"
                      onClick={() => {
                        reset();
                        setMode("forgot");
                      }}
                      className="text-primary hover:underline"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
              )}
              {mode === "confirm" && (
                <div className="flex items-center justify-between text-[11px] pt-1">
                  <button
                    type="button"
                    onClick={resendCode}
                    className="text-primary hover:underline"
                  >
                    Resend code
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      reset();
                      setMode("signin");
                    }}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Back to sign in
                  </button>
                </div>
              )}
              {(mode === "forgot" || mode === "forgot-confirm") && (
                <div className="flex items-center justify-end text-[11px] pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      reset();
                      setMode("signin");
                    }}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Back to sign in
                  </button>
                </div>
              )}

              {error && (
                <div className="rounded-md border border-risk-high/40 bg-risk-high/10 px-3 py-2 text-[12px] text-risk-high">
                  {error}
                </div>
              )}
              {info && (
                <div className="rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-[12px] text-primary">
                  {info}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2.5 text-[13px] font-medium hover:bg-primary/90 glow-primary disabled:opacity-60"
              >
                {ctaLabel}
                {!loading && mode === "signin" && <ArrowRight className="h-4 w-4" />}
              </button>
            </form>

            {/* Sign-up toggle removed: Horizon is invite-only (not self-serve).
                Prospects go through https://www.dravian.io/contact via the
                "Request access" link in the header. */}
          </div>
        </div>

        <FooterStrip />
      </div>

      {/* Right — operations panel */}
      <div className="hidden lg:flex relative overflow-hidden border-l border-border bg-sidebar">
        <div className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(circle_at_20%_20%,oklch(0.62_0.22_25)_0,transparent_40%),radial-gradient(circle_at_80%_70%,oklch(0.7_0.18_220)_0,transparent_45%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:32px_32px]" />

        <div className="relative flex flex-col justify-between p-10 w-full">
          <div>
            <div className="label-eyebrow text-primary">// Live Operations</div>
            <h2 className="mt-4 text-[28px] font-semibold tracking-tight leading-tight max-w-[420px]">
              From signal to scenario — see the whole graph at once.
            </h2>
            <p className="mt-3 text-[13px] text-muted-foreground max-w-[420px]">
              Dravian Horizon unifies entities, signals, and ontologies across geopolitical, supply-chain, financial, and threat domains.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Stat icon={Activity} label="Signals / 24h" value="1,420" />
            <Stat icon={Globe} label="Regions" value="32" />
            <Stat icon={ShieldCheck} label="Uptime" value="99.98%" />
          </div>

          <RotatingDomainCard />
        </div>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, children }: { label: string; icon: typeof Mail; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="label-eyebrow mb-1.5">{label}</div>
      <div className="flex items-center gap-2 rounded-md border border-border bg-surface-elevated px-3 py-2.5 focus-within:border-primary/50 transition">
        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
        {children}
      </div>
    </label>
  );
}

function Stat({ icon: Icon, label, value }: { icon: typeof Mail; label: string; value: string }) {
  return (
    <div className="panel-elevated p-3">
      <Icon className="h-3.5 w-3.5 text-primary" />
      <div className="text-[20px] font-semibold mt-1.5">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

// Rotating card on the login hero. Each entry is a plausible generic scenario
// per Horizon domain — we cycle through them so the login page doesn't hint
// at any specific customer's use case (the old "Escalation in Cauca" was too
// Colombia-specific).
const DOMAIN_SCENARIOS: {
  domain: string;
  label: string;
  title: string;
  blurb: string;
  severity: "low" | "medium" | "high";
}[] = [
  {
    domain: "Geopolitical",
    label: "Regional stability outlook",
    title: "Contested corridor pressure builds",
    blurb: "State and non-state actors converging on a shared transit route over the next 45 days.",
    severity: "high",
  },
  {
    domain: "Supply Chain",
    label: "Supplier concentration risk",
    title: "Tier-2 dependency exposes assembly line",
    blurb: "A single vendor supplies 62% of a critical subassembly across three plants.",
    severity: "medium",
  },
  {
    domain: "Financial",
    label: "Counterparty exposure",
    title: "Regional bank credit widening fast",
    blurb: "Spread moved 240 bps over ninety days; three counterparties concentrated in one sector.",
    severity: "high",
  },
  {
    domain: "Threat",
    label: "Adversary tooling",
    title: "New infrastructure cluster observed",
    blurb: "Overlapping IOCs across four campaigns suggest a shared operator retooling.",
    severity: "medium",
  },
  {
    domain: "Compliance",
    label: "Sanctions posture",
    title: "New designations touch two subsidiaries",
    blurb: "Recent listings intersect the ownership graph of an ongoing joint venture.",
    severity: "medium",
  },
  {
    domain: "Healthcare",
    label: "Cohort surveillance",
    title: "Cluster of adverse events on a shared device",
    blurb: "Signal density on a single implantable model triples across two provider networks.",
    severity: "high",
  },
  {
    domain: "Custom",
    label: "Your ontology",
    title: "Bring your own domain",
    blurb: "Model the entities, signals, and relationships your team actually reasons over.",
    severity: "low",
  },
];

function RotatingDomainCard() {
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * DOMAIN_SCENARIOS.length));
  const [fading, setFading] = useState(false);
  useEffect(() => {
    const t = setInterval(() => {
      setFading(true);
      // Wait for the fade-out, then swap the scenario and fade back in.
      const swap = setTimeout(() => {
        setIdx((i) => (i + 1) % DOMAIN_SCENARIOS.length);
        setFading(false);
      }, 220);
      return () => clearTimeout(swap);
    }, 6000);
    return () => clearInterval(t);
  }, []);

  const s = DOMAIN_SCENARIOS[idx];
  const severityClass =
    s.severity === "high"
      ? "bg-risk-high/15 text-risk-high border-risk-high/30"
      : s.severity === "medium"
      ? "bg-risk-medium/15 text-risk-medium border-risk-medium/30"
      : "bg-risk-low/15 text-risk-low border-risk-low/30";

  return (
    <div
      className={`panel-elevated p-4 transition-opacity duration-300 ${
        fading ? "opacity-0" : "opacity-100"
      }`}
      aria-live="polite"
    >
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">{s.domain} · {s.label}</div>
        <span
          className={`inline-flex items-center rounded px-2 py-0.5 text-[9px] font-semibold tracking-wider uppercase border ${severityClass}`}
        >
          {s.severity}
        </span>
      </div>
      <div className="mt-2 text-[15px] font-semibold">{s.title}</div>
      <div className="text-[12px] text-muted-foreground mt-1">{s.blurb}</div>
      <div className="mt-3 flex items-center gap-1.5">
        {DOMAIN_SCENARIOS.map((_, i) => (
          <span
            key={i}
            className={`h-1 rounded-full transition-all ${
              i === idx ? "w-4 bg-primary" : "w-1 bg-muted-foreground/30"
            }`}
          />
        ))}
      </div>
    </div>
  );
}